import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { INDEMNITY_PERIOD_DEFAULT, LIABILITY_LIMIT_OPTIONS } from 'constants/domain-specific';
import { Construction, CoverItem, DynamicCoverQuestion, PlumbingLicence } from 'types/cover';
import { Nullable } from 'types/utils';
import { isTruthy, isTruthyArray } from 'utils';

export interface BreakdownCover {
  sumInsured?: number;
  numberOfItems?: number;
  deteriorationOfStock?: number;
}

export interface TaxAuditCover {
  sumInsured?: number;
}

/** @todo break these out into details per cover */
export interface CoverDetails {
  /* Dynamic Questions */
  dynamicCoverQuestions: DynamicCoverQuestion[];
  /* Liability */
  liabilityLimit: number;
  useOfContractors: number;
  stampdutyExempted: boolean;
  cpiVIC: boolean;
  cpiQLD: boolean;
  /* Portable Items */
  blanketCovers: CoverItem[];
  specifiedItems: CoverItem[];
  totalCoverValue: number;
  /* Building & Contents */
  construction: Construction;
  yearBuilt: string;
  sprinkler: boolean;
  eps: string;
  /** @clarify what do we pass for this value ? */
  // townWater: string;
  buildingSI: number;
  contentsSI: number;
  flood: boolean;
  indemnityPeriod: number;
  grossIncome: number;
  /* Theft */
  theftContentsSI: number;
  tobaccoSI: number;
  security: string;
  /* Money */
  moneySI: number;
  breakdown: BreakdownCover;
  taxAudit: TaxAuditCover;
  plumbingLicences: PlumbingLicence[];
  /* Validations */
  isValidLiability: boolean;
  isValidBuildingAndContents: boolean;
  isValidBusinessInterruption: boolean;
  isValidMoney: boolean;
  isValidTheft: boolean;
}

const initialState: Nullable<CoverDetails> = {
  /* Dynamic Questions */
  dynamicCoverQuestions: [],
  /* Liability */
  liabilityLimit: LIABILITY_LIMIT_OPTIONS[1].value,
  useOfContractors: null,
  stampdutyExempted: null,
  cpiVIC: null,
  cpiQLD: null,
  /* Portable Items */
  blanketCovers: null,
  specifiedItems: null,
  totalCoverValue: 0,
  /* Building & Contents */
  construction: null,
  eps: null,
  yearBuilt: null,
  sprinkler: null,
  buildingSI: null,
  contentsSI: null,
  flood: null,
  /* Business Interruption */
  indemnityPeriod: INDEMNITY_PERIOD_DEFAULT,
  grossIncome: null,
  /* Theft */
  theftContentsSI: null,
  tobaccoSI: null,
  security: null,
  /* Money */
  moneySI: null,
  plumbingLicences: [],
  /* Validations */
  isValidLiability: false,
  isValidBuildingAndContents: false,
  isValidBusinessInterruption: false,
  isValidMoney: false,
  isValidTheft: false,
  breakdown: null,
  taxAudit: null,
};

/** @todo experiment with moving validate logic into the new effects support in RTK 1.18 */
const validate = (state: Nullable<CoverDetails>) => {
  state.isValidLiability = isTruthyArray([state.liabilityLimit, state.useOfContractors]);

  const validBuildingAndContentsSumInsured =
    isTruthy(state.buildingSI) &&
    isTruthy(state.contentsSI) &&
    /** @note if both SI are 0, we should not allow the customer to proceed */
    state.buildingSI! + state.contentsSI! > 0;

  state.isValidBuildingAndContents =
    validBuildingAndContentsSumInsured &&
    isTruthyArray([state.construction, state.eps, state.yearBuilt, state.sprinkler, state.flood]);

  state.isValidBusinessInterruption = isTruthyArray([state.indemnityPeriod, state.grossIncome]);

  state.isValidTheft = isTruthyArray([state.theftContentsSI, state.security]);

  state.isValidMoney = isTruthyArray([state.moneySI]);
};

export const coverDetailsSlice = createSlice({
  name: 'coverDetails',
  initialState,
  reducers: {
    /*
     * Redux Toolkit allows us to write "mutating" logic in reducers. It doesn't actually mutate the state
     * because it uses the Immer library, which detects changes to a "draft state" and produces a brand new
     * immutable state based off those changes
     */
    updateDynamicCoverQuestions: (state, action: PayloadAction<DynamicCoverQuestion[]>) => {
      state.dynamicCoverQuestions = action.payload;
      validate(state);
    },
    updateLiabilityLimit: (state, action: PayloadAction<number>) => {
      state.liabilityLimit = action.payload;
      validate(state);
    },
    updateUseOfContractors: (state, action: PayloadAction<number | null>) => {
      state.useOfContractors = action.payload;
      validate(state);
    },
    updateStampdutyExempted: (state, action: PayloadAction<boolean | null>) => {
      state.stampdutyExempted = action.payload;
      validate(state);
    },
    clearCpiInsured: (state) => {
      state.cpiQLD = null;
      state.cpiVIC = null;
      validate(state);
    },
    updateBlanketCovers: (state, action: PayloadAction<CoverItem[] | null>) => {
      state.blanketCovers = action.payload;
      validate(state);
    },
    updateSpecifiedItems: (state, action: PayloadAction<CoverItem[] | null>) => {
      state.specifiedItems = action.payload;
      validate(state);
    },
    updateTotalCoverValue: (state, action: PayloadAction<number | null>) => {
      state.totalCoverValue = action.payload;
      validate(state);
    },
    updateConstruction: (state, action: PayloadAction<string>) => {
      state.construction = action.payload as Construction | null;
      validate(state);
    },
    updateEPS: (state, action: PayloadAction<string | null>) => {
      state.eps = action.payload;
      validate(state);
    },
    updateYearBuilt: (state, action: PayloadAction<string | null>) => {
      state.yearBuilt = action.payload;
      validate(state);
    },
    updateSprinkler: (state, action: PayloadAction<boolean | null>) => {
      state.sprinkler = action.payload;
      validate(state);
    },
    updateBuildingSI: (state, action: PayloadAction<number | null>) => {
      state.buildingSI = action.payload;
      validate(state);
    },
    updateContentsSI: (state, action: PayloadAction<number | null>) => {
      state.contentsSI = action.payload;
      validate(state);
    },
    updateFlood: (state, action: PayloadAction<boolean | null>) => {
      state.flood = action.payload;
      validate(state);
    },
    updateIndemnityPeriod: (state, action: PayloadAction<number | null>) => {
      state.indemnityPeriod = action.payload;
      validate(state);
    },
    updateGrossIncome: (state, action: PayloadAction<number | null>) => {
      state.grossIncome = action.payload;
      validate(state);
    },
    updateTheftContentsSI: (state, action: PayloadAction<number | null>) => {
      state.theftContentsSI = action.payload;
      validate(state);
    },
    updateTobaccoSI: (state, action: PayloadAction<number | null>) => {
      state.tobaccoSI = action.payload;
      validate(state);
    },
    updateSecurity: (state, action: PayloadAction<string | null>) => {
      state.security = action.payload;
      validate(state);
    },
    updateMoneySI: (state, action: PayloadAction<number | null>) => {
      state.moneySI = action.payload;
      validate(state);
    },
    updatePlumbingLicences: (state, action: PayloadAction<PlumbingLicence[]>) => {
      state.plumbingLicences = action.payload;
    },
    updateCpiQLD: (state, action: PayloadAction<boolean>) => {
      state.cpiQLD = action.payload;
    },
    updateCpiVIC: (state, action: PayloadAction<boolean>) => {
      state.cpiVIC = action.payload;
    },
    updateBreakdownCover: (state, action: PayloadAction<BreakdownCover>) => {
      state.breakdown = { ...state.breakdown, ...action.payload };
    },
    updateTaxAuditCover: (state, action: PayloadAction<TaxAuditCover>) => {
      state.taxAudit = { ...state.taxAudit, ...action.payload };
    },
    clearCoverDetails: () => initialState,
  },
});

/* Fully typed Action creators are automagically generated for each case reducer function */
export const {
  updateDynamicCoverQuestions,
  updateLiabilityLimit,
  updateUseOfContractors,
  updateStampdutyExempted,
  clearCpiInsured,
  updateBlanketCovers,
  updateSpecifiedItems,
  updateTotalCoverValue,
  updateConstruction,
  updateEPS,
  updateYearBuilt,
  updateSprinkler,
  updateBuildingSI,
  updateContentsSI,
  updateFlood,
  updateIndemnityPeriod,
  updateGrossIncome,
  updateTheftContentsSI,
  updateTobaccoSI,
  updateSecurity,
  updateMoneySI,
  updatePlumbingLicences,
  updateCpiQLD,
  updateCpiVIC,
  updateBreakdownCover,
  updateTaxAuditCover,
  clearCoverDetails,
} = coverDetailsSlice.actions;

export default coverDetailsSlice.reducer;
